export type LostFoundKind = 'lost' | 'found';
export type LostFoundCategory = 'electronics' | 'keys' | 'cards' | 'bags' | 'clothing' | 'other';
export type LostFoundContact = { method: 'email' | 'phone' | 'campus'; value: string };

export interface LostFoundPost {
  id: string;
  kind: LostFoundKind;
  title: string;
  description: string;
  category: LostFoundCategory;
  location: string;
  happenedOn: string;
  reporterName: string;
  contact: LostFoundContact;
  createdAt: string;
  expiresAt: string;
  status: 'open' | 'resolved';
  photoUrl: string | null;
}

export interface LostFoundPostIdentity {
  id: string;
  managementToken: string;
}

export interface LostFoundDraft extends LostFoundPostIdentity {
  kind: LostFoundKind;
  title: string;
  description: string;
  category: LostFoundCategory;
  location: string;
  happenedOn: string;
  reporterName: string;
  contact: LostFoundContact;
  photoDataUrl?: string;
}

export interface LostFoundQuery {
  kind?: 'all' | LostFoundKind;
  status?: 'all' | 'open' | 'resolved';
  q?: string;
  cursor?: string;
  signal?: AbortSignal;
}

export class LostFoundApiError extends Error {
  constructor(message: string, public readonly status: number = 0) {
    super(message);
    this.name = 'LostFoundApiError';
  }
}

const API_PATH = '/api/lost-found';
const OWNERSHIP_KEY = 'lost-found-owned-v1';
const REQUEST_TIMEOUT_MS = 20_000;
const UNAVAILABLE_MESSAGE = 'Lost & Found is unavailable right now. Please try again shortly.';
const ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TOKEN_PATTERN = /^[0-9a-f]{64}$/i;
const CATEGORIES: LostFoundCategory[] = ['electronics', 'keys', 'cards', 'bags', 'clothing', 'other'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isPost(value: unknown): value is LostFoundPost {
  if (!isRecord(value) || !isRecord(value.contact)) return false;
  const textFields = ['id', 'title', 'description', 'location', 'happenedOn', 'reporterName', 'createdAt', 'expiresAt'] as const;
  if (!textFields.every(field => typeof value[field] === 'string')) return false;
  if (!ID_PATTERN.test(value.id as string)) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value.happenedOn as string)) return false;
  if (!Number.isFinite(Date.parse(value.createdAt as string)) || !Number.isFinite(Date.parse(value.expiresAt as string))) return false;
  if (value.kind !== 'lost' && value.kind !== 'found') return false;
  if (value.status !== 'open' && value.status !== 'resolved') return false;
  if (!CATEGORIES.includes(value.category as LostFoundCategory)) return false;
  if (!['email', 'phone', 'campus'].includes(value.contact.method as string) || typeof value.contact.value !== 'string') return false;
  return value.photoUrl === null || value.photoUrl === `${API_PATH}?image=${value.id}`;
}

async function request(payload?: Record<string, unknown>, signal?: AbortSignal): Promise<unknown> {
  const controller = new AbortController();
  let timedOut = false;
  const forwardAbort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', forwardAbort, { once: true });
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  try {
    const { url, body } = payload?.action ? {
      url: API_PATH,
      body: JSON.stringify(payload),
    } : {
      url: typeof payload?.url === 'string' ? payload.url : API_PATH,
      body: undefined,
    };
    const response = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: body ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
      body,
      cache: 'no-store',
      signal: controller.signal,
    });
    // A plain Vite server returns the app HTML for unknown API routes. That is an
    // unavailable service, not a successfully loaded board with no reports.
    if (!response.headers.get('content-type')?.includes('application/json')) {
      throw new LostFoundApiError(UNAVAILABLE_MESSAGE, response.ok ? 503 : response.status);
    }
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      if (controller.signal.aborted) throw new DOMException('The request was cancelled.', 'AbortError');
      throw new LostFoundApiError(UNAVAILABLE_MESSAGE, response.ok ? 502 : response.status);
    }
    if (!response.ok) {
      const message = isRecord(data) && typeof data.error === 'string' && data.error.length <= 500
        ? data.error
        : response.status === 429 ? 'Too many requests. Please wait a moment and try again.' : UNAVAILABLE_MESSAGE;
      throw new LostFoundApiError(message, response.status);
    }
    return data;
  } catch (error) {
    if (signal?.aborted) throw new DOMException('The request was cancelled.', 'AbortError');
    if (timedOut) throw new LostFoundApiError('Lost & Found took too long to respond. Please try again.');
    if (error instanceof LostFoundApiError) throw error;
    throw new LostFoundApiError('Could not connect to Lost & Found. Check your connection and try again.');
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', forwardAbort);
  }
}

export async function getLostFoundPosts(query: LostFoundQuery = {}): Promise<{ posts: LostFoundPost[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (query.kind && query.kind !== 'all') params.set('kind', query.kind);
  if (query.status) params.set('status', query.status);
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.cursor) params.set('cursor', query.cursor);
  const data = await request({ url: `${API_PATH}${params.size ? `?${params}` : ''}` }, query.signal);
  if (!isRecord(data) || !Array.isArray(data.posts) || !data.posts.every(isPost)
    || (data.nextCursor !== null && typeof data.nextCursor !== 'string')) {
    throw new LostFoundApiError('Lost & Found returned an incomplete response. Please try again.', 502);
  }
  return { posts: data.posts, nextCursor: data.nextCursor };
}

function readPostResponse(data: unknown): { post: LostFoundPost } {
  if (!isRecord(data) || !isPost(data.post)) {
    throw new LostFoundApiError('Lost & Found could not confirm your report. Please retry with the same report.', 502);
  }
  return { post: data.post };
}

export async function createLostFoundPost(draft: LostFoundDraft): Promise<{ post: LostFoundPost }> {
  // The caller retains this identity before submitting and reuses it on retries.
  // An interrupted response must never silently generate a duplicate report.
  return readPostResponse(await request({ ...draft, action: 'create' }));
}

export async function resolveLostFoundPost(id: string, managementToken: string): Promise<{ post: LostFoundPost }> {
  return readPostResponse(await request({ action: 'resolve', id, managementToken }));
}

export async function deleteLostFoundPost(id: string, managementToken: string): Promise<void> {
  const data = await request({ action: 'delete', id, managementToken });
  if (!isRecord(data) || data.deleted !== true || data.id !== id) {
    throw new LostFoundApiError('Lost & Found could not confirm removal. Please refresh and try again.', 502);
  }
}

export function createPostIdentity(): LostFoundPostIdentity {
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error('This browser cannot securely create a report. Please open the site using HTTPS in a current browser.');
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  const id = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  const managementToken = Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('');
  return { id, managementToken };
}

/** Only management credentials live on this device. Shared reports live on the server. */
export function readPostIdentities(): Record<string, string> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(OWNERSHIP_KEY) || '{}');
    if (!isRecord(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed).filter(([id, token]) => (
      ID_PATTERN.test(id) && typeof token === 'string' && TOKEN_PATTERN.test(token)
    ))) as Record<string, string>;
  } catch {
    return {};
  }
}

export function rememberPostIdentity(identity: LostFoundPostIdentity): boolean {
  if (!ID_PATTERN.test(identity.id) || !TOKEN_PATTERN.test(identity.managementToken)) return false;
  try {
    localStorage.setItem(OWNERSHIP_KEY, JSON.stringify({ ...readPostIdentities(), [identity.id]: identity.managementToken }));
    return readPostIdentities()[identity.id] === identity.managementToken;
  } catch {
    return false;
  }
}

export function removePostIdentity(id: string): void {
  try {
    const identities = readPostIdentities();
    delete identities[id];
    localStorage.setItem(OWNERSHIP_KEY, JSON.stringify(identities));
  } catch {
    // A disabled storage API must not make a successful server deletion fail.
  }
}

export const LOST_FOUND_MAX_PHOTO_BYTES = 200 * 1024;
export const LOST_FOUND_MAX_SOURCE_PHOTO_BYTES = 10 * 1024 * 1024;

/** Decode and redraw locally to remove metadata, reduce size, and emit JPEG only. */
export async function prepareLostFoundPhoto(file: File): Promise<string> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type.toLowerCase())) {
    throw new Error('Choose a JPEG, PNG, or WebP photo.');
  }
  if (!file.size || file.size > LOST_FOUND_MAX_SOURCE_PHOTO_BYTES) {
    throw new Error('Choose a photo smaller than 10 MB.');
  }
  const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const hasBytes = (expected: number[], offset = 0) => expected.every((byte, index) => header[offset + index] === byte);
  const matchesFormat = file.type.toLowerCase() === 'image/jpeg'
    ? hasBytes([0xff, 0xd8, 0xff])
    : file.type.toLowerCase() === 'image/png'
      ? hasBytes([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
      : hasBytes([0x52, 0x49, 0x46, 0x46]) && hasBytes([0x57, 0x45, 0x42, 0x50], 8);
  if (!matchesFormat) throw new Error('This file does not match its photo format. Choose a JPEG, PNG, or WebP image.');

  const objectUrl = URL.createObjectURL(file);
  const picture = new Image();
  let decodeTimeout: ReturnType<typeof setTimeout> | undefined;
  let canvas: HTMLCanvasElement | undefined;
  try {
    await new Promise<void>((resolve, reject) => {
      picture.onload = () => resolve();
      picture.onerror = () => reject(new Error('This photo could not be opened. Please choose another JPEG, PNG, or WebP image.'));
      decodeTimeout = setTimeout(() => reject(new Error('This photo took too long to open. Try a smaller image.')), 15_000);
      picture.src = objectUrl;
    });
    clearTimeout(decodeTimeout);
    if (!picture.naturalWidth || !picture.naturalHeight) throw new Error('This photo is empty. Please choose another image.');

    canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Your browser could not prepare this photo. Try another browser or submit without a photo.');
    const initialScale = Math.min(1, 1200 / Math.max(picture.naturalWidth, picture.naturalHeight));

    // Both loops are bounded; noisy or unusually detailed photos shrink further
    // instead of producing an oversized request or looping indefinitely.
    for (let sizeAttempt = 0; sizeAttempt < 5; sizeAttempt++) {
      const scale = initialScale * Math.pow(0.8, sizeAttempt);
      canvas.width = Math.max(1, Math.round(picture.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(picture.naturalHeight * scale));
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(picture, 0, 0, canvas.width, canvas.height);
      for (const quality of [0.86, 0.74, 0.62, 0.5]) {
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        if (!dataUrl.startsWith('data:image/jpeg;base64,')) throw new Error('Your browser could not convert this photo. Try another browser.');
        const encoded = dataUrl.slice(dataUrl.indexOf(',') + 1);
        const byteLength = encoded.length * 3 / 4 - (encoded.endsWith('==') ? 2 : encoded.endsWith('=') ? 1 : 0);
        if (byteLength <= LOST_FOUND_MAX_PHOTO_BYTES) return dataUrl;
      }
    }
    throw new Error('This photo is still too large. Please crop it or choose a smaller image.');
  } finally {
    clearTimeout(decodeTimeout);
    picture.onload = null;
    picture.onerror = null;
    picture.src = '';
    URL.revokeObjectURL(objectUrl);
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
